/* eslint-disable no-console */
/* eslint-disable max-len */

/**
 * SafeAtlas - Main Application Entry Point
 * Swiss Workplace Safety & Hazard Assessment Tool
 */

import { TreeBuilder } from './modules/tree/TreeBuilder.js';
import { AIService } from './modules/ai/AIService.js';
import { ExportService } from './modules/export/ExportService.js';
import { LocalizationService } from './modules/localization/LocalizationService.js';
import { ThemeManager } from './utils/ThemeManager.js';
import { StorageManager } from './utils/StorageManager.js';
import { NotificationService } from './utils/NotificationService.js';

import { ConsolePanel } from './utils/ConsolePanel.js';
import { Modal } from './modules/components/Modal.js';
import { TreeEditor } from './modules/components/TreeEditor.js';
import { HazardReport } from './modules/components/HazardReport.js';

// Performance timing for the application
const appStartTime = performance.now();
console.log('🚀 [APP] SafeAtlas loading started at', new Date().toISOString());

const DEFAULT_CUSTOM_NAME = 'Benutzerdefinierter Betrieb';

const HAZARD_CATEGORY_ORDER = [
  'mechanical',
  'falling',
  'electrical',
  'hazardous_substances',
  'fire_explosion',
  'thermal',
  'special_physical',
  'environment',
  'ergonomic',
  'psychosocial',
  'unexpected',
  'energy_failure',
  'organization'
];

const HAZARD_CATEGORY_ALIASES = {
  mechanical: 'mechanical',
  mechanisch: 'mechanical',
  falling: 'falling',
  sturz: 'falling',
  electrical: 'electrical',
  elektrisch: 'electrical',
  hazardous_substances: 'hazardous_substances',
  stoffe: 'hazardous_substances',
  chemisch: 'hazardous_substances',
  fire_explosion: 'fire_explosion',
  brand: 'fire_explosion',
  thermal: 'thermal',
  thermisch: 'thermal',
  special_physical: 'special_physical',
  physikalisch: 'special_physical',
  environment: 'environment',
  umgebung: 'environment',
  ergonomic: 'ergonomic',
  ergonomie: 'ergonomic',
  psychosocial: 'psychosocial',
  psychisch: 'psychosocial',
  unexpected: 'unexpected',
  unerwartet: 'unexpected',
  energy_failure: 'energy_failure',
  energie: 'energy_failure',
  organization: 'organization',
  organisation: 'organization'
};

/**
 * Main Application Class
 * Coordinates all modules and manages application lifecycle
 */
class App {
  constructor() {
    console.log('🏗️ [APP] App constructor called');

    this.isInitialized = false;
    this.currentSection = 'builder';
    this.currentProject = null;
    this.initializationErrors = [];
    this.performanceMetrics = {
      initializationStart: performance.now(),
      serviceInit: {},
      componentInit: {},
      eventSetup: 0
    };

    console.log('📦 [APP] Creating core services...');
    // Initialize core services
    this.storage = new StorageManager();
    this.notifications = new NotificationService();
    this.localization = new LocalizationService();
    this.theme = new ThemeManager();


    console.log('🧩 [APP] Creating feature modules...');
    // Initialize feature modules
    this.treeBuilder = new TreeBuilder();
    this.aiService = new AIService();
    this.exportService = new ExportService();

    console.log('🎨 [APP] Setting up UI component references...');
    // Initialize UI components
    this.treeEditor = null;
    this.hazardReport = null;
    this.consolePanel = null;
    this.spinnerElement = null;
    this.spinnerMessageElement = null;
    this.spinnerCount = 0;
    this.customBusinessNameInput = null;
    this.customBusinessNameWrapper = null;
    this.hazardCategoryInputs = [];
    this.hazardCategorySelectAllButton = null;
    this.hazardCategoryClearButton = null;

    // Extended Profile UI
    this.profileMachinesInput = null;
    this.profileChemicalsInput = null;
    this.profileDescriptionInput = null;
    this.checkShiftwork = null;
    this.checkMobile = null;
    this.checkPregnant = null;
    this.checkYoung = null;
    this.checkAlone = null;

    console.log('🔧 [APP] Binding method contexts...');
    // Bind methods to maintain context
    this.init = this.init.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
    this.handleBusinessTypeChange = this.handleBusinessTypeChange.bind(this);
    this.handleAIGenerate = this.handleAIGenerate.bind(this);
    this.handleAssessmentRun = this.handleAssessmentRun.bind(this);
    this.handleExport = this.handleExport.bind(this);
    this.handleCopyASCII = this.handleCopyASCII.bind(this);
    this.handleExportASCII = this.handleExportASCII.bind(this);
    this.handleAIModelChange = this.handleAIModelChange.bind(this);
    this.ensureAIConfigured = this.ensureAIConfigured.bind(this);
    this.handleCustomBusinessNameInput = this.handleCustomBusinessNameInput.bind(this);
    this.handleHazardCategoryChange = this.handleHazardCategoryChange.bind(this);
    this.handleHazardCategorySelectAll = this.handleHazardCategorySelectAll.bind(this);
    this.handleHazardCategoryClear = this.handleHazardCategoryClear.bind(this);
    this.updateSpinnerMessage = this.updateSpinnerMessage.bind(this);

    console.log('✅ [APP] App constructor completed');
  }

  /**
   * Initialize the application
   */
  async init() {
    try {
      console.log('🚀 [APP] ASCII Tree Builder initialization started at', new Date().toISOString());
      const initStartTime = performance.now();

      // Initialize services with error handling
      console.log('📦 [APP] Service initialization phase starting...');
      try {
        await this.initializeServices();
        this.performanceMetrics.serviceInit.completed = performance.now() - initStartTime;
        console.log('✅ [APP] Service initialization completed in',
          Math.round(this.performanceMetrics.serviceInit.completed), 'ms');
      } catch (serviceError) {
        this.initializationErrors.push({ type: 'service', error: serviceError });
        console.warn('⚠️ [APP] Service initialization had issues:', serviceError);
      }

      // Setup UI event handlers
      console.log('🎯 [APP] Event handler setup starting...');
      this.performanceMetrics.eventSetup = performance.now();
      this.cacheUIElements();
      this.syncBusinessUIState();
      this.setupEventHandlers();
      this.applySavedAIConfig();
      this.initializeConsolePanel();
      this.performanceMetrics.eventSetup = performance.now() - this.performanceMetrics.eventSetup;
      console.log('✅ [APP] Event handlers setup completed in',
        Math.round(this.performanceMetrics.eventSetup), 'ms');

      // Initialize Tree Builder
      console.log('🌳 [APP] Tree Builder initialization starting...');
      this.performanceMetrics.treeBuilderInit = performance.now();
      try {
        this.treeBuilder.init();
        this.performanceMetrics.treeBuilderInit = performance.now() - this.performanceMetrics.treeBuilderInit;
        console.log('✅ [APP] Tree Builder initialized in',
          Math.round(this.performanceMetrics.treeBuilderInit), 'ms');
      } catch (treeBuilderError) {
        this.initializationErrors.push({ type: 'treeBuilder', error: treeBuilderError });
        console.warn('⚠️ [APP] Tree Builder initialization had issues:', treeBuilderError);
      }

      // Initialize UI components
      console.log('🧩 [APP] Component initialization phase starting...');
      this.performanceMetrics.componentInit.start = performance.now();
      try {
        this.initializeComponents();
        this.performanceMetrics.componentInit.completed = performance.now() - this.performanceMetrics.componentInit.start;
        console.log('✅ [APP] Component initialization completed in',
          Math.round(this.performanceMetrics.componentInit.completed), 'ms');
      } catch (componentError) {
        this.initializationErrors.push({ type: 'component', error: componentError });
        console.warn('⚠️ [APP] Component initialization had issues:', componentError);
      }

      // Load saved project if exists
      console.log('💾 [APP] Loading saved project...');
      try {
        await this.loadSavedProject();
        console.log('✅ [APP] Project loading completed');
      } catch (projectError) {
        this.initializationErrors.push({ type: 'project', error: projectError });
        console.warn('⚠️ [APP] Project loading had issues:', projectError);
      }

      // Hide loading screen and show app - CRITICAL STEP
      console.log('🎬 [APP] Showing application...');
      this.showApplication();

      // Ensure default section is visible - CRITICAL
      console.log('🎬 [APP] Ensuring default section visibility...');
      this.ensureVisibleSection();

      // Set initial navigation state
      try {
        console.log('🔄 [APP] Setting initial navigation to builder section...');
        const builderButton = document.getElementById('nav-builder');
        if (builderButton) {
          builderButton.classList.add('active');
          console.log('✅ [APP] Set builder button as active');
        }

        const builderSection = document.getElementById('builder-section');
        if (builderSection) {
          builderSection.classList.add('active');
          console.log('✅ [APP] Set builder section as visible');
        } else {
          console.error('❌ [APP] Builder section not found!');
        }
      } catch (navError) {
        console.warn('⚠️ [APP] Failed to set initial navigation:', navError.message);
      }

      // Mark as initialized
      this.isInitialized = true;
      const totalInitTime = performance.now() - initStartTime;
      this.performanceMetrics.total = totalInitTime;

      console.log('✅ [APP] ASCII Tree Builder fully initialized in',
        Math.round(totalInitTime), 'ms at', new Date().toISOString());

      if (this.initializationErrors.length > 0) {
        console.warn('⚠️ [APP] Initialization completed with',
          this.initializationErrors.length, 'non-critical errors');
      }

      if (this.notifications) {
        this.notifications.success('Application loaded successfully');
      }

      // Log performance summary
      console.log('📊 [APP] Performance Summary:', {
        totalTime: `${Math.round(this.performanceMetrics.total)}ms`,
        services: this.performanceMetrics.serviceInit.completed
          ? `${Math.round(this.performanceMetrics.serviceInit.completed)}ms`
          : 'failed',
        components: this.performanceMetrics.componentInit.completed
          ? `${Math.round(this.performanceMetrics.componentInit.completed)}ms`
          : 'failed',
        treeBuilder: `${Math.round(this.performanceMetrics.treeBuilderInit)}ms`,
        eventHandlers: `${Math.round(this.performanceMetrics.eventSetup)}ms`,
        errors: this.initializationErrors.length
      });
    } catch (error) {
      this.initializationErrors.push({ type: 'critical', error });
      console.error('❌ [APP] Critical initialization failure:', error);

      // Still try to show the app even if initialization fails
      console.log('🛡️ [APP] Attempting to show app despite initialization failure');
      this.showApplication();
      if (this.notifications) {
        this.notifications.error('Failed to initialize application. Please refresh and try again.');
      }
    }
  }

  /**
   * Initialize all core services
   */
  async initializeServices() {
    console.log('📦 Initializing services...');

    // Initialize storage
    try {
      await this.storage.init();
    } catch (error) {
      console.warn('Storage initialization warning:', error);
    }

    // Initialize notification service
    try {
      this.notifications.init();
    } catch (error) {
      console.warn('Notification initialization warning:', error);
    }

    // Initialize localization with saved language or browser default
    try {
      const savedLanguage = this.storage.get('language');
      // Ensure we only pass a valid string or null to localization.init()
      const languageToUse = (typeof savedLanguage === 'string' && savedLanguage)
        ? savedLanguage
        : null;
      await this.localization.init(languageToUse);
    } catch (error) {
      console.warn('Localization initialization warning:', error);
    }

    // Initialize theme
    try {
      const savedTheme = this.storage.get('theme') || 'light';
      this.theme.init(savedTheme);
    } catch (error) {
      console.warn('Theme initialization warning:', error);
    }



    // Initialize AI service with saved settings - don't require API key for basic functionality
    try {
      const savedConfig = this.storage.get('aiConfig', null);
      if (savedConfig) {
        await this.aiService.init({
          model: savedConfig.model || this.getDefaultAIModel(),
          ...savedConfig
        });
      } else {
        await this.aiService.init({ model: this.getDefaultAIModel() });
      }
    } catch (error) {
      console.warn('AI initialization warning:', error);
    }

    console.log('✅ Services initialized (with potential warnings)');
  }

  /**
   * Initialize UI components
   */
  initializeComponents() {
    console.log('🧩 Initializing components...');

    // Initialize Tree Editor
    const treeContainer = document.getElementById('tree-container');
    if (treeContainer) {
      this.treeEditor = new TreeEditor(treeContainer, this.treeBuilder, {
        editable: true,
        draggable: true,
        showControls: false // Using existing toolbar
      }).init();

      // Setup component event listeners
      this.treeEditor.on('nodeSelected', (node) => {
        console.log('Node selected:', node.name);
      });

      this.treeEditor.on('nodeAdded', (node) => {
        this.updateASCIIDisplay();
        this.saveProject();
      });

      this.treeEditor.on('nodeDeleted', (node) => {
        this.updateASCIIDisplay();
        this.saveProject();
      });

      this.treeEditor.on('nodeChanged', () => {
        this.updateASCIIDisplay();
        this.saveProject();
      });
    }

    // Initialize Hazard Report
    const hazardsContainer = document.getElementById('hazards-results');
    if (hazardsContainer) {
      this.hazardReport = new HazardReport(hazardsContainer, {
        printable: true
      }).init();

      // Setup component event listeners
      this.hazardReport.on('exportRequested', (hazardsData) => {
        this.handleExportFromReport(hazardsData);
      });

      this.hazardReport.on('editHazard', (hazard) => {
        this.handleHazardEdit(hazard);
      });

      this.hazardReport.on('hazardDeleted', (hazardId) => {
        this.handleHazardDelete(hazardId);
      });

      this.hazardReport.on('add-risk', async ({ area, category }) => {
        await this.handleAddSingleRisk(area, category);
      });
    }

    console.log('✅ Components initialized');
  }

  cacheUIElements() {
    this.spinnerElement = document.getElementById('global-spinner');
    this.spinnerMessageElement = this.spinnerElement?.querySelector('.spinner-message') || null;
    this.customBusinessNameWrapper = document.getElementById('custom-business-name-wrapper');
    this.customBusinessNameInput = document.getElementById('custom-business-name');
    this.hazardCategoryInputs = Array.from(document.querySelectorAll('.hazard-category-input'));
    this.hazardCategorySelectAllButton = document.getElementById('select-all-categories');
    this.hazardCategoryClearButton = document.getElementById('deselect-all-categories');

    if (this.customBusinessNameInput) {
      const storedName = this.storage.get('customBusinessName', '');
      if (storedName) {
        this.customBusinessNameInput.value = storedName;
      }
    }

    this.restoreHazardCategories();

    // Cache Extended Profile Inputs (Builder)
    this.profileMachinesInput = document.getElementById('profile-machines');
    this.profileChemicalsInput = document.getElementById('profile-chemicals');
    this.profileDescriptionInput = document.getElementById('profile-description');
    this.checkShiftwork = document.getElementById('check-shiftwork');
    this.checkMobile = document.getElementById('check-mobile');
    this.checkPregnant = document.getElementById('check-pregnant');
    this.checkYoung = document.getElementById('check-young');
    this.checkAlone = document.getElementById('check-alone');

    // Cache Extended Profile Inputs (Analysis)
    this.analysisProfileMachines = document.getElementById('analysis-profile-machines');
    this.analysisProfileChemicals = document.getElementById('analysis-profile-chemicals');
    this.analysisProfileDescription = document.getElementById('analysis-profile-description');
    this.analysisCheckShiftwork = document.getElementById('analysis-check-shiftwork');
    this.analysisCheckMobile = document.getElementById('analysis-check-mobile');
    this.analysisCheckPregnant = document.getElementById('analysis-check-pregnant');
    this.analysisCheckYoung = document.getElementById('analysis-check-young');
    this.analysisCheckAlone = document.getElementById('analysis-check-alone');

    // Map fields to their inputs for synchronization
    this.extendedProfileMap = {
      machines: [this.profileMachinesInput, this.analysisProfileMachines],
      chemicals: [this.profileChemicalsInput, this.analysisProfileChemicals],
      description: [this.profileDescriptionInput, this.analysisProfileDescription],
      shiftwork: [this.checkShiftwork, this.analysisCheckShiftwork],
      mobile: [this.checkMobile, this.analysisCheckMobile],
      pregnant: [this.checkPregnant, this.analysisCheckPregnant],
      young: [this.checkYoung, this.analysisCheckYoung],
      alone: [this.checkAlone, this.analysisCheckAlone]
    };

    this.restoreExtendedProfile();
  }

  restoreExtendedProfile() {
    const profile = this.storage.get('extendedProfile', {});

    Object.entries(this.extendedProfileMap).forEach(([field, inputs]) => {
      const value = profile[field];
      inputs.forEach(input => {
        if (!input) return;
        if (input.type === 'checkbox') {
          input.checked = !!value;
        } else {
          input.value = value || '';
        }
      });
    });
  }

  handleProfileChange(field, value) {
    // 1. Update storage
    const profile = this.storage.get('extendedProfile', {});
    profile[field] = value;
    this.storage.set('extendedProfile', profile);

    // 2. Sync other inputs
    const inputs = this.extendedProfileMap[field];
    if (inputs) {
      inputs.forEach(input => {
        if (!input) return;

        if (input.type === 'checkbox') {
          if (input.checked !== value) input.checked = value;
        } else {
          // Only update text inputs if they are not the active element (to preserve cursor)
          // or if the values are strictly different (though active element check is safer)
          if (input !== document.activeElement && input.value !== value) {
            input.value = value;
          }
        }
      });
    }
  }

  // Deprecated but kept for compatibility if called elsewhere, redirected to new logic
  saveExtendedProfile() {
    // This method is no longer the primary way to save.
    // It reconstructs the profile from current inputs and saves it.
    // We'll use the "Builder" inputs as primary source if this is called manually.
    const profile = {
      machines: this.profileMachinesInput?.value || '',
      chemicals: this.profileChemicalsInput?.value || '',
      description: this.profileDescriptionInput?.value || '',
      shiftwork: this.checkShiftwork?.checked || false,
      mobile: this.checkMobile?.checked || false,
      pregnant: this.checkPregnant?.checked || false,
      young: this.checkYoung?.checked || false,
      alone: this.checkAlone?.checked || false
    };
    this.storage.set('extendedProfile', profile);
  }

  syncBusinessUIState() {
    const businessSelect = document.getElementById('business-type');
    const storedType = this.storage.get('businessType', '');
    const effectiveType = businessSelect?.value || storedType;

    if (businessSelect && storedType && businessSelect.value !== storedType) {
      businessSelect.value = storedType;
    }

    this.toggleCustomBusinessNameInput(effectiveType === 'custom');
  }

  restoreHazardCategories() {
    if (!this.hazardCategoryInputs.length) return;

    const stored = this.storage.get('hazardCategories', null);
    const activeValues = Array.isArray(stored) && stored.length > 0
      ? this.normalizeHazardCategories(stored)
      : this.getAllHazardCategories();

    const activeSet = new Set(activeValues);

    this.hazardCategoryInputs.forEach(input => {
      input.checked = activeSet.has(input.value);
    });

    this.storage.set('hazardCategories', activeValues);
  }

  toggleCustomBusinessNameInput(shouldShow) {
    if (!this.customBusinessNameWrapper) return;

    if (shouldShow) {
      this.customBusinessNameWrapper.classList.remove('hidden');
      if (this.customBusinessNameInput) {
        if (!this.customBusinessNameInput.value) {
          const storedName = this.storage.get('customBusinessName', '');
          this.customBusinessNameInput.value = storedName;
        }
        if (typeof this.customBusinessNameInput.focus === 'function') {
          this.customBusinessNameInput.focus({ preventScroll: true });
        }
      }
    } else {
      this.customBusinessNameWrapper.classList.add('hidden');
    }
  }

  getCustomBusinessName() {
    if (!this.customBusinessNameInput) {
      return (this.storage.get('customBusinessName', '') || '').trim();
    }
    return this.customBusinessNameInput.value.trim();
  }

  handleCustomBusinessNameInput(event) {
    const value = event.target.value.trim();
    this.storage.set('customBusinessName', value);

    if ((document.getElementById('business-type')?.value || '') === 'custom') {
      if (this.treeBuilder?.tree) {
        this.treeBuilder.tree.name = value || DEFAULT_CUSTOM_NAME;
        this.updateTreeDisplay();
        this.updateASCIIDisplay();
      }
    }
  }

  getAllHazardCategories() {
    return [...HAZARD_CATEGORY_ORDER];
  }

  normalizeHazardCategories(categories) {
    if (!Array.isArray(categories)) {
      return [...HAZARD_CATEGORY_ORDER];
    }

    const normalizedSet = new Set();

    categories.forEach(category => {
      const mapped = HAZARD_CATEGORY_ALIASES[category];
      if (mapped) {
        normalizedSet.add(mapped);
      }
    });

    const normalized = HAZARD_CATEGORY_ORDER.filter(key => normalizedSet.has(key));
    return normalized.length > 0 ? normalized : [...HAZARD_CATEGORY_ORDER];
  }

  handleHazardCategoryChange() {
    const selected = this.hazardCategoryInputs
      .filter(input => input.checked)
      .map(input => input.value);

    const normalizedSelection = this.normalizeHazardCategories(selected);

    if (normalizedSelection.length === 0) {
      const allCategories = this.getAllHazardCategories();
      this.hazardCategoryInputs.forEach(input => { input.checked = allCategories.includes(input.value); });
      this.storage.set('hazardCategories', allCategories);
      this.notifications?.info('Mindestens eine Gefährdungskategorie muss ausgewählt bleiben.');
      return;
    }

    this.storage.set('hazardCategories', normalizedSelection);
  }

  handleHazardCategorySelectAll() {
    const allCategories = this.getAllHazardCategories();
    this.hazardCategoryInputs.forEach(input => { input.checked = allCategories.includes(input.value); });
    this.storage.set('hazardCategories', allCategories);
    this.notifications?.info('Alle Gefährdungskategorien aktiviert');
  }

  handleHazardCategoryClear() {
    const defaults = ['mechanical', 'falling', 'electrical'];

    this.hazardCategoryInputs.forEach(input => {
      input.checked = defaults.includes(input.value);
    });

    this.storage.set('hazardCategories', defaults);
    this.notifications?.info('Gefährdungskategorien auf Standard zurückgesetzt');
  }

  showGlobalSpinner(message = 'Bitte warten...') {
    this.spinnerCount = Math.max(this.spinnerCount, 0) + 1;
    if (!this.spinnerElement) return;

    this.spinnerElement.classList.remove('hidden');
    if (this.spinnerMessageElement) {
      this.spinnerMessageElement.textContent = message;
    }
  }

  hideGlobalSpinner() {
    this.spinnerCount = Math.max(this.spinnerCount - 1, 0);
    if (!this.spinnerElement || this.spinnerCount > 0) {
      return;
    }
    this.spinnerElement.classList.add('hidden');
  }

  updateSpinnerMessage(message) {
    if (this.spinnerMessageElement && this.spinnerElement && !this.spinnerElement.classList.contains('hidden')) {
      this.spinnerMessageElement.textContent = message;
    }
  }

  /**
   * Setup UI event handlers
   */
  setupEventHandlers() {
    console.log('🎯 [APP] Setting up event handlers...');
    const setupStartTime = performance.now();
    let handlersAttached = 0;
    let handlersFailed = 0;

    try {
      // Navigation buttons - CRITICAL for basic functionality
      console.log('📍 [APP] Setting up navigation handlers...');
      const navElements = [
        { id: 'nav-builder', handler: () => this.handleNavigation('builder'), name: 'Builder' },
        { id: 'nav-assessment', handler: () => this.handleNavigation('assessment'), name: 'Assessment' },
        { id: 'nav-export', handler: () => this.handleNavigation('export'), name: 'Export' }
      ];

      navElements.forEach(({ id, handler, name }) => {
        const element = document.getElementById(id);
        if (element) {
          console.log(`✅ [APP] Found ${name} button (${id}), attaching listener`);
          element.addEventListener('click', handler);
          handlersAttached++;
        } else {
          handlersFailed++;
          console.error(`❌ [APP] ${name} button (${id}) not found in DOM!`);
        }
      });

      // Builder controls
      console.log('🔧 [APP] Setting up builder controls...');
      const builderElements = [
        { id: 'business-type', event: 'change', handler: this.handleBusinessTypeChange, name: 'Business Type' },
        { id: 'ai-model', event: 'change', handler: this.handleAIModelChange, name: 'AI Model' },
        { id: 'custom-business-name', event: 'input', handler: this.handleCustomBusinessNameInput, name: 'Custom Business Name' },
        { id: 'ai-generate', event: 'click', handler: this.handleAIGenerate, name: 'AI Generate' },
        { id: 'add-node', event: 'click', handler: () => this.handleAddNode(), name: 'Add Node' },
        { id: 'delete-node', event: 'click', handler: () => this.handleDeleteNode(), name: 'Delete Node' },
        { id: 'expand-all', event: 'click', handler: () => this.handleExpandAll(), name: 'Expand All' },
        { id: 'collapse-all', event: 'click', handler: () => this.handleCollapseAll(), name: 'Collapse All' },
        { id: 'copy-ascii', event: 'click', handler: () => this.handleCopyASCII(), name: 'Copy ASCII' },
        { id: 'export-ascii', event: 'click', handler: () => this.handleExportASCII(), name: 'Export ASCII' }
      ];

      builderElements.forEach(({ id, event, handler, name }) => {
        const element = document.getElementById(id);
        if (element) {
          console.log(`✅ [APP] Found ${name} control (${id}), attaching ${event} listener`);
          element.addEventListener(event, handler);
          handlersAttached++;
        } else {
          handlersFailed++;
          console.error(`❌ [APP] ${name} control (${id}) not found in DOM!`);
        }
      });

      // Assessment controls
      console.log('🛡️ [APP] Setting up assessment controls...');
      const assessmentElements = [
        { id: 'run-assessment', event: 'click', handler: this.handleAssessmentRun, name: 'Run Assessment' },
        { id: 'hazards-per-area', event: 'input', handler: this.handleHazardsPerAreaChange, name: 'Hazards Per Area' },
        { id: 'assessment-profile', event: 'change', handler: this.handleAssessmentProfileChange, name: 'Assessment Profile' }
      ];

      assessmentElements.forEach(({ id, event, handler, name }) => {
        const element = document.getElementById(id);
        if (element) {
          console.log(`✅ [APP] Found ${name} control (${id}), attaching ${event} listener`);
          element.addEventListener(event, handler);
          handlersAttached++;
        } else {
          handlersFailed++;
          console.error(`❌ [APP] ${name} control (${id}) not found in DOM!`);
        }
      });

      // Export controls
      console.log('📊 [APP] Setting up export controls...');
      const exportElements = [
        { id: 'export-excel', event: 'click', handler: () => this.handleExport('excel'), name: 'Export Excel' },
        { id: 'export-csv', event: 'click', handler: () => this.handleExport('csv'), name: 'Export CSV' },
        { id: 'load-project-btn', event: 'click', handler: () => document.getElementById('project-upload')?.click(), name: 'Load Project' }
      ];

      exportElements.forEach(({ id, event, handler, name }) => {
        const element = document.getElementById(id);
        if (element) {
          console.log(`✅ [APP] Found ${name} control (${id}), attaching ${event} listener`);
          element.addEventListener(event, handler);
          handlersAttached++;
        } else {
          // Only warn for load button as it might not exist yet
          if (id !== 'load-project-btn') {
            handlersFailed++;
            console.error(`❌ [APP] ${name} control (${id}) not found in DOM!`);
          }
        }
      });

      // File upload handler
      const fileInput = document.getElementById('project-upload');
      if (fileInput) {
        fileInput.addEventListener('change', (e) => this.handleLoadProject(e));
      }

      // Header controls
      console.log('⚙️ [APP] Setting up header controls...');
      const headerElements = [
        { id: 'theme-toggle', event: 'click', handler: () => this.handleThemeToggle(), name: 'Theme Toggle' },
        { id: 'language-selector', event: 'click', handler: () => this.handleLanguageToggle(), name: 'Language Selector' },
        { id: 'settings-button', event: 'click', handler: () => this.handleSettings(), name: 'Settings' }
      ];

      headerElements.forEach(({ id, event, handler, name }) => {
        const element = document.getElementById(id);
        if (element) {
          console.log(`✅ [APP] Found ${name} control (${id}), attaching ${event} listener`);
          element.addEventListener(event, handler);
          handlersAttached++;
        } else {
          handlersFailed++;
          console.error(`❌ [APP] ${name} control (${id}) not found in DOM!`);
        }
      });

      // Handle URL hash changes for navigation
      window.addEventListener('hashchange', () => {
        const hash = window.location.hash.slice(1);
        if (hash && ['builder', 'assessment', 'export'].includes(hash)) {
          this.handleNavigation(hash);
        }
      });

      // Handle initial hash on load
      const initialHash = window.location.hash.slice(1);
      if (initialHash && ['builder', 'assessment', 'export'].includes(initialHash)) {
        // Small delay to ensure DOM is ready
        setTimeout(() => this.handleNavigation(initialHash), 100);
      }

      // Hazard category controls
      this.hazardCategoryInputs.forEach(input => {
        input.addEventListener('change', this.handleHazardCategoryChange);
      });

      // Extended Profile Listeners (Sync)
      if (this.extendedProfileMap) {
        Object.entries(this.extendedProfileMap).forEach(([field, inputs]) => {
          inputs.forEach(input => {
            if (input) {
              // For text inputs
              input.addEventListener('input', (e) => {
                // For checkboxes, 'input' might not trigger on change, but 'change' does.
                // For text, 'input' is better for realtime sync.
                if (input.type !== 'checkbox') {
                  this.handleProfileChange(field, e.target.value);
                }
              });

              // For checkboxes and final text commits
              input.addEventListener('change', (e) => {
                const value = input.type === 'checkbox' ? input.checked : input.value;
                this.handleProfileChange(field, value);
              });
            }
          });
        });
      }

      // Specific Risk Buttons
      document.querySelectorAll('.btn-add-specific-risk').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const source = e.currentTarget.dataset.source;
          this.handleAddSpecificRisk(source);
        });
      });


      if (this.hazardCategorySelectAllButton) {
        this.hazardCategorySelectAllButton.addEventListener('click', this.handleHazardCategorySelectAll);
      }

      if (this.hazardCategoryClearButton) {
        this.hazardCategoryClearButton.addEventListener('click', this.handleHazardCategoryClear);
      }

      const setupTime = performance.now() - setupStartTime;
      console.log('📊 [APP] Event handler setup completed:', {
        attached: handlersAttached,
        failed: handlersFailed,
        time: `${Math.round(setupTime)}ms`,
        success: handlersFailed === 0
      });

      if (handlersFailed > 0) {
        console.warn('⚠️ [APP] Some event handlers failed to attach - UI elements may be missing');
      }
    } catch (error) {
      const setupTime = performance.now() - setupStartTime;
      console.error('❌ [APP] Critical error during event handler setup:', error.message, {
        time: `${Math.round(setupTime)}ms`
      });
      this.initializationErrors.push({ type: 'eventHandlers', error });
    }
  }

  /**
   * Show application and hide loading screen
   */
  showApplication() {
    console.log('🎬 Showing application...');

    const loadingScreen = document.getElementById('loading-screen');
    const appContainer = document.getElementById('app');

    console.log('📍 Elements found:', {
      loadingScreen: !!loadingScreen,
      appContainer: !!appContainer
    });

    if (loadingScreen) {
      console.log('📍 Hiding loading screen');
      loadingScreen.style.display = 'none';
      loadingScreen.style.opacity = '0';
      loadingScreen.style.visibility = 'hidden';
    }

    if (appContainer) {
      console.log('📍 Showing app container');
      appContainer.style.display = 'block';
      appContainer.style.visibility = 'visible';
      appContainer.style.opacity = '1';
    }

    // Force a reflow to ensure styles are applied
    document.body.getBoundingClientRect();

    console.log('✅ Application should now be visible');
  }

  /**
   * Handle navigation between sections
   */
  handleNavigation(section) {
    console.log('🔄 [APP] Navigation requested:', section);
    const navStartTime = performance.now();

    try {
      // Validate section parameter
      if (!section || typeof section !== 'string') {
        console.error('❌ [APP] Invalid section parameter:', section);
        return;
      }

      console.log('📍 [APP] Finding DOM elements for navigation...');

      // Find and update navigation buttons
      const buttons = document.querySelectorAll('.nav-button');
      console.log('📍 [APP] Found', buttons.length, 'nav button elements');

      buttons.forEach(button => {
        button.classList.remove('active');
        console.log('📍 [APP] Removed active class from button:', button.id);
      });

      const targetButton = document.getElementById(`nav-${section}`);
      if (targetButton) {
        targetButton.classList.add('active');
        console.log('✅ [APP] Added active class to nav button:', `nav-${section}`);
      } else {
        console.warn('⚠️ [APP] Target nav button not found:', `nav-${section}`);
      }

      // Find and update sections
      const sections = document.querySelectorAll('.content-section');
      console.log('📍 [APP] Found', sections.length, 'content section elements');

      sections.forEach(sectionEl => {
        sectionEl.classList.remove('active');
        console.log('📍 [APP] Hidden section:', sectionEl.id);
      });

      const targetSection = document.getElementById(`${section}-section`);
      if (targetSection) {
        targetSection.classList.add('active');
        console.log('✅ [APP] Made section visible:', `${section}-section`);

        // Log what's in the section for debugging
        const sectionContent = targetSection.innerHTML.substring(0, 200);
        console.log('📍 [APP] Section preview:', {
          sectionId: `${section}-section`,
          contentPreview: `${sectionContent}...`,
          hasContent: targetSection.children.length > 0
        });
      } else {
        console.error('❌ [APP] Target section not found:', `${section}-section`);
      }

      // Update current section
      this.currentSection = section;

      // Save current section to storage
      try {
        this.storage.set('currentSection', section);
        console.log('💾 [APP] Saved current section to storage:', section);
      } catch (storageError) {
        console.warn('⚠️ [APP] Failed to save section to storage:', storageError.message);
      }

      // Update URL hash for bookmarking
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${section}`);
      }

      const navTime = performance.now() - navStartTime;
      console.log('✅ [APP] Navigation completed in', Math.round(navTime), 'ms:', {
        targetSection: section,
        buttonsUpdated: buttons.length,
        sectionsUpdated: sections.length,
        activeSection: this.currentSection
      });
    } catch (error) {
      const navTime = performance.now() - navStartTime;
      console.error('❌ [APP] Navigation failed:', error.message, {
        targetSection: section,
        time: `${Math.round(navTime)}ms`,
        stack: error.stack
      });

      // Try to ensure at least one section is visible
      this.ensureVisibleSection();
    }
  }

  /**
   * Ensure at least one section is visible (fallback)
   */
  ensureVisibleSection() {
    console.log('🛡️ [APP] Running fallback section visibility check...');

    const activeSection = document.querySelector('.content-section.active');
    if (activeSection) {
      console.log('✅ [APP] Section already visible:', activeSection.id);
      return;
    }

    // Try to show the builder section by default
    const builderSection = document.getElementById('builder-section');
    if (builderSection) {
      builderSection.classList.add('active');
      console.log('✅ [APP] Made builder section visible as fallback');
    } else {
      console.error('❌ [APP] No sections found to make visible!');
    }
  }

  /**
   * Handle business type selection change
   */
  handleBusinessTypeChange(event) {
    const businessType = event.target.value;
    this.storage.set('businessType', businessType || '');
    this.toggleCustomBusinessNameInput(businessType === 'custom');

    if (!businessType) {
      return;
    }

    console.log('🏢 Business type selected:', businessType);

    // Apply business type template
    this.applyBusinessTemplate(businessType);
  }

  /**
   * Apply business type template
   */
  applyBusinessTemplate(businessType) {
    // Clear existing tree
    this.treeBuilder.init();

    if (businessType === 'custom') {
      const customName = this.getCustomBusinessName();
      this.treeBuilder.tree.name = customName || DEFAULT_CUSTOM_NAME;
      if (!customName) {
        this.notifications.info('Bitte geben Sie einen Betriebsnamen ein, um eine individuelle Struktur zu erzeugen.');
      }
      this.updateTreeDisplay();
      this.updateASCIIDisplay();
      this.saveProject();
      return;
    }

    const templates = {
      restaurant: {
        name: 'Restaurant',
        children: [
          { name: 'Küche', type: 'area' },
          { name: 'Speisesaal', type: 'area' },
          { name: 'Theke/Kasse', type: 'area' },
          { name: 'Lager', type: 'area' },
          { name: 'Büro', type: 'area' }
        ]
      },
      retail: {
        name: 'Einzelhandel',
        children: [
          { name: 'Verkaufsfläche', type: 'area' },
          { name: 'Kasse', type: 'area' },
          { name: 'Lagerraum', type: 'area' },
          { name: 'Personalraum', type: 'area' },
          { name: 'Büro', type: 'area' }
        ]
      },
      manufacturing: {
        name: 'Produktion',
        children: [
          { name: 'Produktionshalle', type: 'area' },
          { name: 'Lager', type: 'area' },
          { name: 'Werkstatt', type: 'area' },
          { name: 'Verwaltung', type: 'area' },
          { name: 'Sozialräume', type: 'area' }
        ]
      },
      office: {
        name: 'Büro',
        children: [
          { name: 'Open Space Büros', type: 'area' },
          { name: 'Konferenzräume', type: 'area' },
          { name: 'Teeküche', type: 'area' },
          { name: 'Archiv/Lager', type: 'area' },
          { name: 'Technikraum', type: 'area' }
        ]
      }
    };

    const template = templates[businessType];
    if (template) {
      if (template.name) {
        this.treeBuilder.tree.name = template.name;
      }
      // Add children to the tree
      template.children.forEach(child => {
        this.treeBuilder.addNode('root', child);
      });

      console.log('📋 Template applied for:', businessType);
      this.notifications.success(`Template loaded: ${businessType}`);
    } else {
      // Add a default node if no template found
      this.treeBuilder.addNode('root', { name: 'Allgemeiner Bereich', type: 'area' });
      this.notifications.info('Default template applied');
    }

    // Save the project after template application
    this.updateTreeDisplay();
    this.updateASCIIDisplay();
    this.saveProject();
  }

  /**
   * Handle AI generation
   */
  async handleAIGenerate() {
    console.log('🤖 [APP] AI Generate button clicked');
    const startTime = performance.now();

    // Check if tree builder is initialized
    if (!this.treeBuilder || !this.treeBuilder.tree) {
      console.error('❌ [APP] Tree builder not initialized, forcing initialization...');
      this.treeBuilder.init();
    }

    try {
      // Show loading notification
      const loadingToast = this.notifications.loading('Struktur wird generiert...');

      const businessType = document.getElementById('business-type').value;
      if (!businessType) {
        this.notifications.completeLoading(loadingToast, 'Bitte wählen Sie zuerst eine Betriebsart aus', 'error', 5000);
        return;
      }

      const customName = businessType === 'custom' ? this.getCustomBusinessName() : '';
      if (businessType === 'custom' && !customName) {
        this.notifications.completeLoading(loadingToast, 'Bitte geben Sie einen Betriebsnamen ein, bevor die KI gestartet wird.', 'warning', 5000);
        this.toggleCustomBusinessNameInput(true);
        return;
      }

      const aiReady = await this.ensureAIConfigured();
      if (!aiReady) {
        this.notifications.completeLoading(loadingToast, 'KI-Generierung abgebrochen', 'warning', 4000);
        return;
      }

      console.log('🔧 [APP] Generating structure for business type:', businessType);

      // Try AI generation first
      let spinnerActive = false;
      try {
        console.log('🤖 [APP] Attempting AI generation...');
        const spinnerLabel = businessType === 'custom'
          ? `KI erstellt räumliche Struktur für ${customName}`
          : 'KI erstellt räumliche Struktur...';
        this.showGlobalSpinner(spinnerLabel);
        spinnerActive = true;

        const profile = document.getElementById('assessment-profile')?.value || 'standard';
        const treeData = await this.aiService.generateTreeStructure(businessType, customName, profile);
        if (businessType === 'custom' && customName) {
          treeData.name = customName;
        }
        // Apply to tree builder
        this.treeBuilder.import(treeData);
        console.log('✅ [APP] AI generation successful, applied to tree');
      } catch (aiError) {
        // Fallback to predefined templates
        if (spinnerActive) {
          this.hideGlobalSpinner();
          spinnerActive = false;
        }
        console.log('🔄 [APP] Using fallback templates due to AI error:', aiError.message);
        this.notifications.warning(`KI-Generierung fehlgeschlagen: ${aiError.message}`);
        this.applyBusinessTemplate(businessType);
        console.log('✅ [APP] Fallback template applied');
      } finally {
        if (spinnerActive) {
          this.hideGlobalSpinner();
        }
      }

      // Update UI
      console.log('🎨 [APP] Updating displays...');
      this.updateTreeDisplay();
      this.updateASCIIDisplay();

      this.notifications.completeLoading(loadingToast, 'Struktur erfolgreich generiert!', 'success', 3000);

      const generateTime = performance.now() - startTime;
      console.log('✅ [APP] Structure generation completed in', Math.round(generateTime), 'ms');
    } catch (error) {
      const generateTime = performance.now() - startTime;
      console.error('❌ [APP] Generation failed:', error.message, {
        time: `${Math.round(generateTime)}ms`
      });

      // Try to apply template even if there's an error
      try {
        const businessType = document.getElementById('business-type').value || 'restaurant';
        console.log('🛡️ [APP] Emergency fallback - applying default template for:', businessType);
        this.applyBusinessTemplate(businessType);
        this.updateTreeDisplay();
        this.updateASCIIDisplay();
        this.notifications.success('Struktur mit Vorlage generiert');
      } catch (fallbackError) {
        console.error('❌ [APP] Even fallback failed:', fallbackError.message);
        this.notifications.error('Strukturerstellung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    }
  }

  /**
   * Run safety assessment
   */
  async handleAssessmentRun() {
    const loadingToast = this.notifications.loading('Sicherheitsanalyse wird ausgeführt...');
    let spinnerActive = false;
    let hazardCount = 0;
    try {
      const treeStructure = this.treeBuilder.export();
      const config = this.getAssessmentConfig();

      if (!treeStructure || !treeStructure.children || treeStructure.children.length === 0) {
        this.notifications.completeLoading(loadingToast, 'Bitte erstellen Sie zuerst eine Betriebsstruktur.', 'warning', 4000);
        return;
      }

      const totalAreas = Math.max(this.countTreeNodes(treeStructure) - 1, 0);
      const hazardsPerArea = Math.max(parseInt(config.hazardsPerArea, 10) || 0, 0);
      const expectedHazards = hazardsPerArea > 0 ? Math.max(totalAreas * hazardsPerArea, hazardsPerArea) : 0;

      const aiReady = await this.ensureAIConfigured();
      if (!aiReady) {
        this.notifications.completeLoading(loadingToast, 'Analyse abgebrochen', 'warning', 4000);
        return;
      }

      const initialSpinnerMessage = expectedHazards > 0
        ? `Sicherheitsanalyse läuft... (0 / ${expectedHazards} Gefährdungen)`
        : 'Sicherheitsanalyse wird berechnet...';
      this.showGlobalSpinner(initialSpinnerMessage);
      spinnerActive = true;
      const analysisStart = performance.now();
      let tokenCount = 0;
      let hazardProgress = 0;

      // Gather Extended Profile Data
      const profileData = {
        machines: this.profileMachinesInput?.value || '',
        chemicals: this.profileChemicalsInput?.value || '',
        description: this.profileDescriptionInput?.value || '',
        conditions: {
          shiftwork: this.checkShiftwork?.checked || false,
          mobile: this.checkMobile?.checked || false,
          pregnant: this.checkPregnant?.checked || false,
          young: this.checkYoung?.checked || false,
          alone: this.checkAlone?.checked || false
        }
      };

      // Inject Special Risks Node if needed
      const analysisTree = JSON.parse(JSON.stringify(treeStructure));
      const hasSpecialNode = analysisTree.children?.some(c => c.type === 'special_risks');

      if (!hasSpecialNode) {
        if (!analysisTree.children) analysisTree.children = [];
        analysisTree.children.push({
          name: "Spezielle Gefährdungen & Betriebsmittel",
          type: "special_risks",
          children: []
        });
      }

      const hazardsData = await this.aiService.analyzeHazards(analysisTree, {
        hazardsPerArea,
        profile: config.profile,
        mode: config.mode || 'standard',
        categories: Array.isArray(config.categories) ? config.categories : this.getAllHazardCategories(),
        profileData: profileData,
        onToken: (delta, aggregatedText) => {
          tokenCount++;
          if (tokenCount % 12 === 0) {
            if (aggregatedText && expectedHazards > 0) {
              const matches = aggregatedText.match(/"riskLevel"\s*:/g);
              if (matches) {
                hazardProgress = Math.max(hazardProgress, matches.length);
              }
            }
            if (expectedHazards > 0) {
              const progressText = `Sicherheitsanalyse läuft... (${Math.min(hazardProgress, expectedHazards)} / ${expectedHazards} Gefährdungen)`;
              this.updateSpinnerMessage(progressText);
              console.log('📡 [AI] Streaming Fortschritt:', { tokenCount, hazardProgress, expectedHazards });
            } else {
              this.updateSpinnerMessage('Sicherheitsanalyse wird berechnet...');
            }
          }
        }
      });

      const analysisDuration = Math.round(performance.now() - analysisStart);
      const totalNodes = totalAreas;
      hazardCount = Array.isArray(hazardsData?.hazards) ? hazardsData.hazards.length : 0;
      if (expectedHazards > 0) {
        this.updateSpinnerMessage(`Sicherheitsanalyse läuft... (${Math.min(hazardCount, expectedHazards)} / ${expectedHazards} Gefährdungen)`);
      }
      hazardsData.meta = {
        generatedAt: new Date().toISOString(),
        hazardsPerArea: config.hazardsPerArea,
        selectedCategories: Array.isArray(config.categories) ? [...config.categories] : this.getAllHazardCategories(),
        totalAreas: totalNodes,
        projectName: treeStructure.name || 'Betriebsstruktur',
        profile: config.profile,
        tokensConsumed: tokenCount,
        hazardsIdentified: hazardCount,
        analysisDuration
      };

      if (this.hazardReport) {
        this.hazardReport.setHazards(hazardsData);
      }

      this.handleNavigation('assessment');

      await this.ensureProject();
      this.currentProject.hazards = hazardsData;
      await this.saveProject();

      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Analyse abgeschlossen', 'success', 2500);
      }
      this.notifications.success('Safety assessment completed');
    } catch (error) {
      console.error('❌ Assessment failed:', error);
      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Analyse fehlgeschlagen', 'error', 4000);
      }
      this.notifications.error(`Assessment failed: ${error.message}`);
    } finally {
      if (spinnerActive) {
        const completionMessage = hazardCount > 0
          ? `Analyse abgeschlossen – ${hazardCount} Gefährdungen identifiziert`
          : 'Analyse abgeschlossen';
        this.updateSpinnerMessage(completionMessage);
        await new Promise(resolve => setTimeout(resolve, 600));
        this.hideGlobalSpinner();
      }
    }
  }

  /**
   * Handle adding a single risk to an area
   */
  async handleAddSingleRisk(area, category = null) {
    const loadingToast = this.notifications.loading(`Generiere weitere Gefährdung für "${area}"...`);
    try {
      const config = this.getAssessmentConfig();

      // Add category if specified
      if (category) {
        config.category = category;
      }

      const currentHazards = this.currentProject.hazards.hazards;

      // Get extended profile for special risks
      const extendedProfile = this.storage.get('extendedProfile', {});

      const newHazard = await this.aiService.generateSingleHazard(area, currentHazards, config, extendedProfile);

      // Generate a unique ID
      const maxId = currentHazards.reduce((max, h) => {
        const num = parseInt(h.id.replace(/\D/g, ''), 10) || 0;
        return Math.max(max, num);
      }, 0);
      newHazard.id = `hazard_${String(maxId + 1).padStart(3, '0')}`;

      // Add to list
      this.currentProject.hazards.hazards.push(newHazard);

      // Update UI
      this.hazardReport.setHazards(this.currentProject.hazards);

      // Save
      await this.saveProject();

      this.notifications.completeLoading(loadingToast, 'Neue Gefährdung hinzugefügt', 'success');
    } catch (error) {
      console.error('❌ Failed to add single risk:', error);
      this.notifications.completeLoading(loadingToast, 'Fehler beim Hinzufügen', 'error');
    }
  }


  /**
   * Handle adding a specific risk based on a source (machine, chemical, condition)
   */
  async handleAddSpecificRisk(source) {
    let sourceType = '';
    let sourceValue = '';

    // Determine source value based on type
    const profile = this.storage.get('extendedProfile', {});

    if (source === 'machines') {
      sourceType = 'Maschine/Anlage';
      sourceValue = profile.machines;
    } else if (source === 'chemicals') {
      sourceType = 'Chemikalie/Stoff';
      sourceValue = profile.chemicals;
    } else if (source === 'description') {
      sourceType = 'Betriebsbeschreibung';
      sourceValue = profile.description;
    } else if (source.startsWith('condition-')) {
      sourceType = 'Arbeitsbedingung';
      const conditionKey = source.replace('condition-', '');

      const conditionLabels = {
        shiftwork: 'Schichtarbeit / Nachtarbeit',
        mobile: 'Mobile Arbeitsplätze / Aussendienst',
        pregnant: 'Schwangere / Stillende Frauen',
        young: 'Jugendliche / Lernende',
        alone: 'Alleinarbeit'
      };

      // Only proceed if the condition is actually checked
      if (!profile[conditionKey]) {
        this.notifications.info(`Bitte aktivieren Sie zuerst "${conditionLabels[conditionKey]}"`);
        return;
      }
      sourceValue = conditionLabels[conditionKey];
    }

    if (!sourceValue || sourceValue.trim() === '') {
      this.notifications.info('Bitte geben Sie zuerst einen Wert ein.');
      return;
    }

    const loadingToast = this.notifications.loading(`Analysiere "${sourceValue}"...`);

    try {
      // Ensure "Spezielle Gefährdungen" node exists in hazards
      let specialArea = "Spezielle Gefährdungen & Betriebsmittel";

      // Check if we need to create the area in the project structure if it doesn't exist?
      // For now, we assume it exists or we just add the hazard to it.

      const currentHazards = this.currentProject.hazards.hazards;

      // Generate specific hazard
      const newHazard = await this.aiService.generateSpecificHazard(sourceType, sourceValue, currentHazards);

      // Generate ID
      const maxId = currentHazards.reduce((max, h) => {
        const num = parseInt(h.id.replace(/\D/g, ''), 10) || 0;
        return Math.max(max, num);
      }, 0);
      newHazard.id = `hazard_${String(maxId + 1).padStart(3, '0')}`;

      // Add to list
      this.currentProject.hazards.hazards.push(newHazard);

      // Update UI
      this.hazardReport.setHazards(this.currentProject.hazards);

      // Save
      await this.saveProject();

      this.notifications.completeLoading(loadingToast, 'Spezifische Gefährdung hinzugefügt', 'success');

      // Scroll to the new hazard (optional, but nice)
      // We can try to scroll to the bottom of the special risks section

    } catch (error) {
      console.error('❌ Failed to add specific risk:', error);
      this.notifications.completeLoading(loadingToast, 'Fehler beim Hinzufügen', 'error');
    }
  }

  /**
   * Handle hazard edit
   */
  async handleHazardEdit(hazard) {
    try {
      const updatedHazard = await Modal.editHazard(hazard);

      if (updatedHazard) {
        // Update in project
        const index = this.currentProject.hazards.hazards.findIndex(h => h.id === hazard.id);
        if (index > -1) {
          this.currentProject.hazards.hazards[index] = updatedHazard;

          // Update UI
          this.hazardReport.setHazards(this.currentProject.hazards);

          // Save
          await this.saveProject();

          this.notifications.success('Gefährdung aktualisiert');
        }
      }
    } catch (error) {
      console.error('❌ Failed to edit hazard:', error);
      this.notifications.error('Fehler beim Bearbeiten der Gefährdung');
    }
  }

  /**
   * Handle hazard deletion
   */
  async handleHazardDelete(hazardId) {
    try {
      // Hazard is already removed from HazardReport's internal state,
      // but we need to sync it with our main project state
      const index = this.currentProject.hazards.hazards.findIndex(h => h.id === hazardId);
      if (index > -1) {
        this.currentProject.hazards.hazards.splice(index, 1);

        // Save
        await this.saveProject();

        this.notifications.success('Gefährdung gelöscht');
      }
    } catch (error) {
      console.error('❌ Failed to delete hazard:', error);
      this.notifications.error('Fehler beim Löschen der Gefährdung');
    }
  }

  /**
   * Handle export
   */
  async handleExport(format, hazardsData = null) {
    let loadingToast = null;
    try {
      const hazardsToExport = hazardsData || this.currentProject?.hazards;

      if (!hazardsToExport) {
        throw new Error('No assessment data to export. Please run assessment first.');
      }

      loadingToast = this.notifications.loading(`Exporting to ${format.toUpperCase()}...`);

      await this.ensureProject();

      const metadata = {
        companyName: document.getElementById('company-name')?.value || '',
        assessorName: document.getElementById('assessor-name')?.value || '',
        treeStructure: this.currentProject.treeStructure,
        generatedAt: new Date().toISOString()
      };

      // Gather full project state for saving
      const projectState = {
        tree: this.treeBuilder.export(),
        hazards: hazardsToExport,
        metadata: metadata,
        extendedProfile: this.storage.get('extendedProfile', {}),
        hazardCategories: this.storage.get('hazardCategories', []),
        businessType: this.storage.get('businessType', ''),
        customBusinessName: this.storage.get('customBusinessName', ''),
        version: '1.0.0'
      };

      let result;
      if (format === 'excel') {
        result = await this.exportService.exportToExcel(hazardsToExport, metadata, projectState);
      } else {
        result = await this.exportService.exportToCSV(hazardsToExport, metadata);
      }

      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Export abgeschlossen', 'success', 2000);
      }
      this.notifications.success(`Report exported: ${result.fileName}`);
    } catch (error) {
      console.error('❌ Export failed:', error);
      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Export fehlgeschlagen', 'error', 4000);
      }
      this.notifications.error(`Export failed: ${error.message}`);
    }
  }

  /**
   * Handle loading project from file
   */
  async handleLoadProject(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Reset input value so same file can be selected again
    event.target.value = '';

    let loadingToast = null;
    try {
      loadingToast = this.notifications.loading('Loading project...');

      const result = await this.exportService.importProjectFromExcel(file);
      if (!result.success || !result.data) {
        throw new Error('Invalid project file');
      }

      const state = result.data;

      // Restore Tree
      if (state.tree) {
        this.treeBuilder.import(state.tree);
        this.updateTreeDisplay();
        this.updateASCIIDisplay();
      }

      // Restore Extended Profile
      if (state.extendedProfile) {
        this.storage.set('extendedProfile', state.extendedProfile);
        this.restoreExtendedProfile();
      }

      // Restore Hazard Categories
      if (state.hazardCategories) {
        this.storage.set('hazardCategories', state.hazardCategories);
        this.restoreHazardCategories();
      }

      // Restore Business Type
      if (state.businessType) {
        this.storage.set('businessType', state.businessType);
        const businessSelect = document.getElementById('business-type');
        if (businessSelect) businessSelect.value = state.businessType;
      }

      // Restore Custom Name
      if (state.customBusinessName) {
        this.storage.set('customBusinessName', state.customBusinessName);
        if (this.customBusinessNameInput) {
          this.customBusinessNameInput.value = state.customBusinessName;
        }
        this.toggleCustomBusinessNameInput(state.businessType === 'custom');
      }

      // Restore Hazards
      if (state.hazards) {
        this.currentProject = {
          ...this.currentProject,
          hazards: state.hazards,
          treeStructure: state.tree ? this.treeBuilder.toASCII() : ''
        };

        if (this.hazardReport) {
          this.hazardReport.setHazards(state.hazards);
        }
      }

      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Project loaded successfully', 'success', 2000);
      }

      // Switch to builder view to show loaded structure
      this.handleNavigation('builder');

    } catch (error) {
      console.error('❌ Load failed:', error);
      if (loadingToast) {
        this.notifications.completeLoading(loadingToast, 'Load failed', 'error', 4000);
      }
      this.notifications.error(`Failed to load project: ${error.message}`);
    }
  }

  async handleCopyASCII() {
    if (!this.treeBuilder || !this.treeBuilder.toASCII) {
      this.notifications.error('ASCII-Struktur nicht verfügbar');
      return;
    }

    const asciiText = this.treeBuilder.toASCII();
    if (!asciiText || asciiText.trim().length === 0) {
      this.notifications.info('Keine ASCII-Struktur vorhanden');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(asciiText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = asciiText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!successful) {
          throw new Error('execCommand copy failed');
        }
      }
      this.notifications.success('ASCII-Struktur kopiert');
    } catch (error) {
      console.error('❌ [APP] Failed to copy ASCII:', error);
      this.notifications.error('Kopieren fehlgeschlagen');
    }
  }

  handleExportASCII() {
    if (!this.treeBuilder || !this.treeBuilder.toASCII) {
      this.notifications.error('ASCII-Struktur nicht verfügbar');
      return;
    }

    const asciiText = this.treeBuilder.toASCII();
    if (!asciiText || asciiText.trim().length === 0) {
      this.notifications.info('Keine ASCII-Struktur vorhanden');
      return;
    }

    try {
      const blob = new Blob([asciiText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ascii-structure-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.notifications.success('ASCII-Datei heruntergeladen');
    } catch (error) {
      console.error('❌ [APP] Failed to export ASCII:', error);
      this.notifications.error('Export der ASCII-Datei fehlgeschlagen');
    }
  }

  initializeConsolePanel() {
    const consoleElement = document.getElementById('console-output');
    if (!consoleElement) {
      console.warn('⚠️ [APP] Console output element not found');
      return;
    }

    if (!this.consolePanel) {
      this.consolePanel = new ConsolePanel(consoleElement, { maxEntries: 400 });
      this.consolePanel.init();
    }

    const clearButton = document.getElementById('console-clear');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.consolePanel?.clear();
      });
    }
  }

  async ensureAIConfigured() {
    if (this.aiService?.isConfigured && this.aiService.isConfigured()) {
      return true;
    }

    const storedConfig = this.storage.get('aiConfig', null);
    if (storedConfig?.apiKey && !this.aiService.isConfigured()) {
      await this.aiService.init(storedConfig);
      if (this.aiService.isConfigured()) {
        return true;
      }
      this.notifications.warning('Gespeicherter API-Schlüssel konnte nicht übernommen werden. Bitte neu eingeben.');
    }

    const wantsKey = window.confirm('Für die KI-Generierung wird ein gültiger OpenAI-API-Schlüssel benötigt. Möchten Sie jetzt einen eingeben?');
    if (!wantsKey) {
      this.notifications.info('KI-Generierung wurde abgebrochen – Vorlage wird weiterhin angeboten.');
      return false;
    }

    const apiKey = window.prompt('Bitte geben Sie Ihren OpenAI API Key ein:');
    if (!apiKey || !apiKey.trim()) {
      this.notifications.warning('Kein API Key eingegeben.');
      return false;
    }

    const config = {
      ...(storedConfig || {}),
      apiKey: apiKey.trim(),
      model: this.getSelectedAIModel()
    };

    await this.aiService.init(config);
    if (this.aiService.isConfigured()) {
      this.storage.set('aiConfig', config);
      this.notifications.success('OpenAI API Key gespeichert.');
      return true;
    }

    this.notifications.error('API-Schlüssel konnte nicht validiert werden. Bitte prüfen Sie den Key.');
    return false;
  }

  /**
   * Update tree display using component
   */
  updateTreeDisplay() {
    console.log('🌳 [APP] Updating tree display...');
    if (this.treeEditor) {
      console.log('✅ [APP] TreeEditor available, rendering tree');
      this.treeEditor.renderTree();
    } else {
      console.warn('⚠️ [APP] TreeEditor not available');
    }
  }

  /**
   * Update ASCII display
   */
  updateASCIIDisplay() {
    console.log('🎨 [APP] Updating ASCII display...');
    const asciiOutput = document.getElementById('ascii-output');
    if (!asciiOutput) {
      console.warn('⚠️ [APP] ASCII output element not found');
      return;
    }

    if (!this.treeBuilder || !this.treeBuilder.toASCII) {
      console.warn('⚠️ [APP] TreeBuilder not available for ASCII conversion');
      return;
    }

    const asciiText = this.treeBuilder.toASCII();
    asciiOutput.textContent = asciiText;
    console.log('✅ [APP] ASCII display updated, length:', asciiText.length);
  }

  /**
   * Handle export from hazard report
   */
  async handleExportFromReport(hazardsData) {
    try {
      const format = await Modal.confirm(
        'Choose export format:',
        {
          title: 'Export Report',
          confirmText: 'Excel',
          cancelText: 'CSV'
        }
      );

      this.handleExport(format ? 'excel' : 'csv', hazardsData);
    } catch (error) {
      // User cancelled
    }
  }

  /**
   * Handle hazard editing
   */
  handleEditHazard(hazard) {
    // Implementation would show hazard editing dialog
    console.log('Editing hazard:', hazard);
    this.notifications.info('Hazard editing feature coming soon');
  }

  /**
   * Get assessment configuration
   */
  getAssessmentConfig() {
    const hazardsPerArea = parseInt(document.getElementById('hazards-per-area')?.value) || 3;
    const profile = document.getElementById('assessment-profile')?.value || 'standard';
    const mode = document.getElementById('assessment-mode')?.value || 'standard';
    const selectedCategories = this.hazardCategoryInputs.length
      ? this.hazardCategoryInputs.filter(input => input.checked).map(input => input.value)
      : this.getAllHazardCategories();
    const normalizedCategories = this.normalizeHazardCategories(selectedCategories);

    return {
      hazardsPerArea,
      profile,
      mode,
      categories: normalizedCategories
    };
  }

  countTreeNodes(node) {
    if (!node) return 0;
    const children = Array.isArray(node.children) ? node.children : [];
    return 1 + children.reduce((total, child) => total + this.countTreeNodes(child), 0);
  }

  getDefaultAIModel() {
    return 'gpt-4o';
  }

  getSelectedAIModel() {
    const modelSelect = document.getElementById('ai-model');
    return modelSelect?.value || this.getDefaultAIModel();
  }

  applySavedAIConfig() {
    const modelSelect = document.getElementById('ai-model');
    if (!modelSelect) return;

    const storedConfig = this.storage.get('aiConfig', {}) || {};
    const desiredModel = storedConfig.model || this.getDefaultAIModel();

    const hasOption = Array.from(modelSelect.options).some(option => option.value === desiredModel);
    modelSelect.value = hasOption ? desiredModel : this.getDefaultAIModel();

    this.aiService.updateConfig({ model: modelSelect.value });
  }

  handleAIModelChange(event) {
    const model = event.target.value;
    const storedConfig = this.storage.get('aiConfig', {}) || {};
    storedConfig.model = model;
    this.storage.set('aiConfig', storedConfig);
    this.aiService.updateConfig({ model });

    const label = event.target.selectedOptions?.[0]?.textContent || model;
    this.notifications.info(`KI-Modell gewechselt zu: ${label}`);
  }

  /**
   * Handle theme toggle
   */
  handleThemeToggle() {
    console.log('🎨 [APP] Theme toggle button clicked');
    const startTime = performance.now();

    try {
      if (!this.theme) {
        console.error('❌ [APP] Theme manager not initialized');
        this.notifications.error('Theme manager not available');
        return;
      }

      const newTheme = this.theme.toggleTheme();
      this.storage.set('theme', newTheme);

      const themeTime = performance.now() - startTime;
      console.log('✅ [APP] Theme toggled to', newTheme, 'in', Math.round(themeTime), 'ms');
      this.notifications.success(`Theme switched to ${newTheme}`);
    } catch (error) {
      const themeTime = performance.now() - startTime;
      console.error('❌ [APP] Theme toggle failed:', error.message, {
        time: `${Math.round(themeTime)}ms`
      });
      this.notifications.error('Failed to switch theme');
    }
  }

  /**
   * Handle language toggle
   */
  handleLanguageToggle() {
    console.log('🌐 [APP] Language toggle button clicked');
    const supportedLanguages = ['de', 'fr', 'it', 'en'];
    const currentLang = this.localization?.currentLanguage || 'de';
    const currentIndex = supportedLanguages.indexOf(currentLang);
    const nextIndex = (currentIndex + 1) % supportedLanguages.length;
    const nextLang = supportedLanguages[nextIndex];

    try {
      if (this.localization && this.localization.setLanguage) {
        this.localization.setLanguage(nextLang);
        this.storage.set('language', nextLang);
        console.log('✅ [APP] Language switched to:', nextLang);
        this.notifications.success(`Sprache gewechselt zu: ${nextLang.toUpperCase()}`);
      } else {
        console.warn('⚠️ [APP] Localization not available, default language is Deutsch');
        this.notifications.info('Language switch not available - using Deutsch');
      }
    } catch (error) {
      console.error('❌ [APP] Language toggle failed:', error.message);
      this.notifications.error('Failed to switch language');
    }
  }

  /**
   * Handle settings
   */
  handleSettings() {
    console.log('⚙️ [APP] Settings button clicked');
    const startTime = performance.now();

    try {
      // For now, show a simple notification with current settings
      const settings = {
        theme: this.storage.get('theme') || 'light',
        language: this.localization?.currentLanguage || 'de',
        businessType: document.getElementById('business-type')?.value || 'none',
        treeNodes: this.treeBuilder?.export()?.children?.length || 0
      };

      console.log('📊 [APP] Current settings:', settings);
      this.notifications.info(`Settings: Theme=${settings.theme}, Language=${settings.language}, Tree nodes=${settings.treeNodes}`);

      const settingsTime = performance.now() - startTime;
      console.log('✅ [APP] Settings action completed in', Math.round(settingsTime), 'ms');
    } catch (error) {
      const settingsTime = performance.now() - startTime;
      console.error('❌ [APP] Settings action failed:', error.message, {
        time: `${Math.round(settingsTime)}ms`
      });
      this.notifications.error('Failed to load settings');
    }
  }

  /**
   * Handle tree node operations
   */
  handleAddNode() {
    const startTime = performance.now();
    console.log('➕ [APP] Add Node button clicked');

    if (!this.treeEditor || !this.treeBuilder) {
      console.error('❌ [APP] Cannot add node - tree editor or builder not initialized:', {
        treeEditor: !!this.treeEditor,
        treeBuilder: !!this.treeBuilder
      });
      this.notifications.error('Tree editor not initialized');
      return;
    }

    try {
      // Get selected node or use root if none selected
      const parentId = this.treeBuilder.currentNode?.id || 'root';
      const currentTree = this.treeBuilder.export();

      console.log('🔧 [APP] Adding new node:', {
        parentId,
        currentRootChildren: currentTree.children.length,
        currentNodeName: this.treeBuilder.currentNode?.name || 'none'
      });

      // Add a simple node
      const nodeData = {
        name: `Neuer Bereich ${currentTree.children.length + 1}`,
        type: 'area'
      };

      const newNode = this.treeBuilder.addNode(parentId, nodeData);

      if (newNode) {
        this.treeBuilder.currentNode = newNode;
        console.log('✅ [APP] Node added successfully:', {
          nodeId: newNode.id,
          nodeName: newNode.name,
          parentId
        });

        // Update UI
        console.log('🎨 [APP] Updating tree display and ASCII...');
        this.updateTreeDisplay();
        this.updateASCIIDisplay();
        this.saveProject();

        const totalTime = performance.now() - startTime;
        console.log('⏱️ [APP] Add node operation completed in', Math.round(totalTime), 'ms');
        this.notifications.success('Node added successfully');
      } else {
        console.error('❌ [APP] Failed to add node - treeBuilder returned null');
        this.notifications.error('Failed to add node');
      }
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error('❌ [APP] Failed to add node:', error.message, {
        time: `${Math.round(totalTime)}ms`,
        stack: error.stack
      });
      this.notifications.error('Failed to add node');
    }
  }

  handleDeleteNode() {
    if (!this.treeEditor || !this.treeBuilder) {
      this.notifications.error('Tree editor not initialized');
      return;
    }

    try {
      if (!this.treeBuilder.currentNode || this.treeBuilder.currentNode.id === 'root') {
        this.notifications.warning('Cannot delete root node');
        return;
      }

      const nodeId = this.treeBuilder.currentNode.id;
      const success = this.treeBuilder.removeNode(nodeId);

      if (success) {
        this.treeBuilder.currentNode = null;
        this.updateTreeDisplay();
        this.updateASCIIDisplay();
        this.saveProject();
        this.notifications.success('Node deleted successfully');
      } else {
        this.notifications.error('Failed to delete node');
      }
    } catch (error) {
      console.error('Failed to delete node:', error);
      this.notifications.error('Failed to delete node');
    }
  }

  handleExpandAll() {
    console.log('📂 [APP] Expand All button clicked');
    const startTime = performance.now();

    try {
      if (!this.treeEditor || !this.treeBuilder) {
        console.error('❌ [APP] Cannot expand - tree components not initialized');
        this.notifications.error('Tree components not ready');
        return;
      }

      // Expand all nodes in the tree
      console.log('🌳 [APP] Expanding all nodes in tree...');

      // For now, just re-render the tree (expand functionality would be in TreeEditor)
      this.updateTreeDisplay();
      this.updateASCIIDisplay();

      const expandTime = performance.now() - startTime;
      console.log('✅ [APP] Expand all completed in', Math.round(expandTime), 'ms');
      this.notifications.success('All nodes expanded');
    } catch (error) {
      const expandTime = performance.now() - startTime;
      console.error('❌ [APP] Expand all failed:', error.message, {
        time: `${Math.round(expandTime)}ms`
      });
      this.notifications.error('Failed to expand nodes');
    }
  }

  handleCollapseAll() {
    console.log('📁 [APP] Collapse All button clicked');
    const startTime = performance.now();

    try {
      if (!this.treeEditor || !this.treeBuilder) {
        console.error('❌ [APP] Cannot collapse - tree components not initialized');
        this.notifications.error('Tree components not ready');
        return;
      }

      // Collapse all nodes in the tree
      console.log('🌳 [APP] Collapsing all nodes in tree...');

      // For now, just re-render the tree (collapse functionality would be in TreeEditor)
      this.updateTreeDisplay();
      this.updateASCIIDisplay();

      const collapseTime = performance.now() - startTime;
      console.log('✅ [APP] Collapse all completed in', Math.round(collapseTime), 'ms');
      this.notifications.success('All nodes collapsed');
    } catch (error) {
      const collapseTime = performance.now() - startTime;
      console.error('❌ [APP] Collapse all failed:', error.message, {
        time: `${Math.round(collapseTime)}ms`
      });
      this.notifications.error('Failed to collapse nodes');
    }
  }

  handleHazardsPerAreaChange(event) {
    const count = event.target.value;
    document.getElementById('hazards-count').textContent = count;
  }

  handleAssessmentProfileChange(event) {
    console.log('📊 Assessment profile changed:', event.target.value);
  }

  /**
   * Save current project
   */
  async saveProject() {
    if (!this.currentProject) {
      this.currentProject = {
        id: this.generateProjectId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }

    this.currentProject.treeStructure = this.treeBuilder.export();
    this.currentProject.updatedAt = new Date().toISOString();

    this.storage.set('currentProject', this.currentProject);
  }

  async ensureProject() {
    if (this.currentProject) {
      return this.currentProject;
    }
    await this.saveProject();
    return this.currentProject;
  }

  /**
   * Load saved project
   */
  async loadSavedProject() {
    try {
      const savedProject = this.storage.get('currentProject');
      if (savedProject) {
        this.currentProject = savedProject;

        // Restore tree structure
        if (this.currentProject.treeStructure) {
          this.treeBuilder.import(this.currentProject.treeStructure);
          this.updateTreeDisplay();
          this.updateASCIIDisplay();
        }

        // Restore hazards in report component
        if (this.currentProject.hazards && this.hazardReport) {
          this.hazardReport.setHazards(this.currentProject.hazards);
        }

        console.log('📂 Project loaded from storage');
      }
    } catch (error) {
      console.warn('Failed to load saved project:', error);
    }
  }

  /**
   * Detect browser language
   */
  detectBrowserLanguage() {
    return navigator.language.split('-')[0].toLowerCase() || 'de';
  }

  /**
   * Generate unique project ID
   */
  generateProjectId() {
    return `project_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Initialize application when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  const domReadyTime = performance.now() - appStartTime;
  console.log('🌐 [DOM] DOM Content Loaded event fired', {
    elapsed: `${Math.round(domReadyTime)}ms`,
    timestamp: new Date().toISOString(),
    userAgent: `${navigator.userAgent.substring(0, 50)}...`
  });

  console.log('🎬 [DOM] Checking DOM elements...');

  // Show the app immediately as a fallback
  const loadingScreen = document.getElementById('loading-screen');
  const appContainer = document.getElementById('app');

  console.log('📋 [DOM] Element status:', {
    loadingScreen: !!loadingScreen,
    appContainer: !!appContainer,
    loadingScreenDisplay: loadingScreen?.style.display,
    appContainerDisplay: appContainer?.style.display
  });

  if (appContainer) {
    console.log('🚀 [DOM] Forcing app display immediately');
    appContainer.style.display = 'block';
    appContainer.style.visibility = 'visible';

    // Log DOM structure
    const treeContainer = document.getElementById('tree-container');
    const asciiOutput = document.getElementById('ascii-output');
    console.log('🗂️ [DOM] App structure elements:', {
      treeContainer: !!treeContainer,
      asciiOutput: !!asciiOutput,
      navBuilder: !!document.getElementById('nav-builder'),
      navAssessment: !!document.getElementById('nav-assessment'),
      navExport: !!document.getElementById('nav-export')
    });
  }

  if (loadingScreen) {
    loadingScreen.style.display = 'none';
    console.log('🎭 [DOM] Loading screen hidden');
  }

  const constructorStartTime = performance.now();
  console.log('🏗️ [APP] Creating App instance...');

  const app = new App();

  const constructionTime = performance.now() - constructorStartTime;
  console.log('✅ [APP] App instance created in', Math.round(constructionTime), 'ms');

  const initStartTime = performance.now();
  console.log('🚀 [APP] Starting app initialization...');

  await app.init();

  const totalInitTime = performance.now() - initStartTime;
  const totalTime = performance.now() - appStartTime;

  console.log('🏁 [APP] Full application startup completed:', {
    initTime: `${Math.round(totalInitTime)}ms`,
    totalTime: `${Math.round(totalTime)}ms`,
    errorsCaught: app.initializationErrors?.length || 0
  });

  // Make app instance globally accessible for debugging
  window.app = app;
  console.log('🔧 [APP] App instance available globally as window.app');
});

// Also try to load the app after window load as a backup
window.addEventListener('load', () => {
  console.log('🪟 Window loaded - checking app visibility...');
  const appContainer = document.getElementById('app');
  const loadingScreen = document.getElementById('loading-screen');

  if (appContainer && appContainer.style.display === 'none') {
    console.log('🛠️ Forcing app display from window load event');
    appContainer.style.display = 'block';
    if (loadingScreen) {
      loadingScreen.style.display = 'none';
    }
  }
});

// Export app for potential module usage
export default App;
